/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import Constants from 'configs/Constants';
import React from 'react';
import Thumbnail from './Thumbnail';

export enum Side {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

interface Props {
  side: Side;
  mainThumbnailIndex?: number;
  mainSpriteUrl?: string;
  spriteUrl?: string;
  isInvisible?: boolean;
}

const renderPrevThumbnails = (
  mainThumbnailIndex: number,
  spriteUrl?: string,
  mainSpriteUrl?: string,
  isInvisible?: boolean
) => {
  const remainingSpriteThumbnails = Constants.NUMBER_THUMBNAILS_PER_SPRITE - (mainThumbnailIndex + 1);
  const requiresSideSprite =
    remainingSpriteThumbnails >= Constants.NUMBER_THUMBNAILS_PER_SPRITE - Constants.VISUAL_SCRUBBER_SIDE_CARDS;

  if (!requiresSideSprite) {
    return (
      <>
        <Thumbnail spriteUrl={mainSpriteUrl} index={mainThumbnailIndex - 2} />
        <Thumbnail spriteUrl={mainSpriteUrl} index={mainThumbnailIndex - 1} />
      </>
    );
  }

  const referenceIndex = Constants.NUMBER_THUMBNAILS_PER_SPRITE - 1;

  if (mainThumbnailIndex === 1) {
    return (
      <>
        <Thumbnail spriteUrl={spriteUrl} index={referenceIndex} isInvisible={isInvisible} />
        <Thumbnail spriteUrl={mainSpriteUrl} index={mainThumbnailIndex - 1} />
      </>
    );
  }

  return (
    <>
      <Thumbnail spriteUrl={spriteUrl} index={referenceIndex - 1} isInvisible={isInvisible} />
      <Thumbnail spriteUrl={spriteUrl} index={referenceIndex} isInvisible={isInvisible} />
    </>
  );
};

const renderNextThumbnails = (mainThumbnailIndex: number, spriteUrl?: string, mainSpriteUrl?: string) => {
  const remainingSpriteThumbnails = Constants.NUMBER_THUMBNAILS_PER_SPRITE - (mainThumbnailIndex + 1);
  const requiresSideSprite = Constants.VISUAL_SCRUBBER_SIDE_CARDS > remainingSpriteThumbnails;

  if (!requiresSideSprite) {
    return (
      <>
        <Thumbnail spriteUrl={mainSpriteUrl} index={mainThumbnailIndex + 1} />
        <Thumbnail spriteUrl={mainSpriteUrl} index={mainThumbnailIndex + 2} />
      </>
    );
  }

  const referenceIndex = 0;

  if (remainingSpriteThumbnails === 1) {
    return (
      <>
        <Thumbnail spriteUrl={mainSpriteUrl} index={mainThumbnailIndex + 1} />
        <Thumbnail spriteUrl={spriteUrl} index={referenceIndex} />
      </>
    );
  }

  return (
    <>
      <Thumbnail spriteUrl={spriteUrl} index={referenceIndex} />
      <Thumbnail spriteUrl={spriteUrl} index={referenceIndex + 1} />
    </>
  );
};

export default ({ side, spriteUrl, mainSpriteUrl, mainThumbnailIndex, isInvisible }: Props) => {
  if (mainThumbnailIndex === undefined) {
    return (
      <>
        <Thumbnail isInvisible={isInvisible} />
        <Thumbnail isInvisible={isInvisible} />
      </>
    );
  }

  if (side === Side.LEFT) {
    return renderPrevThumbnails(mainThumbnailIndex, spriteUrl, mainSpriteUrl, isInvisible);
  }

  return renderNextThumbnails(mainThumbnailIndex, spriteUrl, mainSpriteUrl);
};
